import { useState, Dispatch, SetStateAction } from 'react';
import { usePrevious } from './usePrevious';

/**
 * A hook that tracks both the current and previous values of state, similar to useState.
 * Returns a tuple with an object containing both the current and previous values, and a setter function.
 *
 * @param initialValue - The initial value for the state
 * @returns A tuple of the form [{value, previous}, setValue] where:
 *   - value: The current state value
 *   - previous: The previous state value (undefined on first render)
 *   - setValue: A function to update the state value (works like useState's setter)
 *
 * @example
 * const [{value, previous}, setValue] = useWithPreviousState(0);
 * // value = 0, previous = undefined (first render)
 * setValue(5);
 * // value = 5, previous = 0 (next render)
 *
 * @example
 * const [{value, previous}, setValue] = useWithPreviousState('');
 * useEffect(() => {
 *   if (value !== previous) {
 *     console.log(`Value changed from ${previous} to ${value}`);
 *   }
 * }, [value, previous]);
 */
export const useWithPreviousState = <T>(
  initialValue: T | (() => T),
): [{ value: T; previous: T | undefined }, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(initialValue);
  const previous = usePrevious<T>(value);

  return [{ value, previous }, setValue];
};
